import React, { useEffect, useRef, useState, Fragment } from "react";
import clsx from "clsx";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import Layout from "../layouts";
import { useConfigStore, useAccountStore } from "../store";
import axios, { apiBaseUrl } from "../services/http";

export default function Login() {
  const { config } = useConfigStore();
  const { me, fetch } = useAccountStore();
  const ws = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [readCardActive, setReadCardActive] = useState(false);

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [login, setLogin] = useState({
    status: "idle",
    data: null,
    error: null,
  });
  const [tokenLogin, setTokenLogin] = useState({
    status: "idle",
    data: null,
    error: null,
  });

  useEffect(() => {
    if (me) {
      navigate("/");
    }
  }, [me, navigate]);

  const onSubmitForm = async (e) => {
    e.preventDefault();

    setLogin((state) => ({
      ...state,
      status: "pending",
      error: null,
    }));

    await axios.get(`${apiBaseUrl}/sanctum/csrf-cookie`);

    try {
      await axios.post(`${apiBaseUrl}/api/login`, formData);
      setLogin((state) => ({
        ...state,
        status: "succeeded",
      }));

      await fetch();

      if (
        location.state &&
        location.state.from &&
        location.state.from.pathname
      ) {
        navigate(location.state.from.pathname);
      } else {
        navigate("/");
      }
    } catch (error) {
      setLogin((state) => ({
        ...state,
        status: "failed",
        error: error.response.data,
      }));
    }
  };

  const onShowReadCard = () => {
    setReadCardActive(true);

    ws.current = new WebSocket("wss://app01.lib.eduhk.hk/booking-login");
    ws.current.onmessage = async (event) => {
      if (tokenLogin.status !== "pending") {
        setTokenLogin((state) => ({
          ...state,
          status: "pending",
          error: null,
        }));

        await axios.get(`${apiBaseUrl}/sanctum/csrf-cookie`);

        try {
          await axios.post(`${apiBaseUrl}/api/login-token`, {
            token: event.data,
          });
          setTokenLogin((state) => ({
            ...state,
            status: "succeeded",
          }));

          await fetch();

          closeWs();

          if (
            location.state &&
            location.state.from &&
            location.state.from.pathname
          ) {
            navigate(location.state.from.pathname);
          } else {
            navigate("/");
          }
        } catch (error) {
          setTokenLogin((state) => ({
            ...state,
            status: "failed",
            error: error.response.data,
          }));
        }
      }
    };
  };

  const closeWs = () => {
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
  };

  const onReadCardClose = () => {
    setReadCardActive(false);
    closeWs();
  };

  return (
    <Layout>
      <div className="container mx-auto">
        <div className="lg:py-5 lg:px-0 py-4 px-4">
          <h1 className="font-bold text-lg">Log in with your EdUHK account:</h1>

          <div className="flex tall:flex-col short:space-x-10 items-center justify-center py-10">
            <div className="shadow-lg max-w-lg w-full tall:mb-10">
              <div className="bg-white border px-8 py-8">
                {login.error && (
                  <div
                    class="flex p-4 mb-8 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
                    role="alert"
                  >
                    <svg
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      className="flex-shrink-0 inline w-5 h-5 mr-3"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                      ></path>
                    </svg>
                    <div className="space-y-2">
                      {Array.isArray(login.error.message)
                        ? login.error.message.map((message, i) => (
                            <p key={i}>{message}</p>
                          ))
                        : login.error.message}
                    </div>
                  </div>
                )}
                <form onSubmit={onSubmitForm}>
                  <div className="space-y-5">
                    <div>
                      <label>EdUHK Network Username:</label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="username"
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-orange-500 focus:border-orange-500 block w-full p-2.5"
                          placeholder="e.g. s1234567"
                          value={formData.username}
                          onChange={(e) =>
                            setFormData((state) => ({
                              ...state,
                              username: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <label>Password:</label>
                      <div className="mt-1">
                        <input
                          type="password"
                          name="password"
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-orange-500 focus:border-orange-500 block w-full p-2.5"
                          value={formData.password}
                          onChange={(e) =>
                            setFormData((state) => ({
                              ...state,
                              password: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-center">
                      <button
                        type="submit"
                        className={clsx(
                          "text-white bg-orange-500 hover:bg-orange-600 focus:ring-4 focus:outline-none focus:ring-orange-300 font-medium rounded-lg w-full px-5 py-3 text-center",
                          login.status === "pending"
                            ? "opacity-50 pointer-events-none"
                            : ""
                        )}
                        disabled={login.status === "pending"}
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            {config.use_card && (
              <div className="flex tall:flex-col short:space-x-10">
                <div className="login-or short:before:left-1/2 short:before:top-0 short:before:w-[2px] short:before:h-full short:flex short:items-center tall:mb-5">
                  <span>or</span>
                </div>
                <div className="flex flex-col items-center">
                  <img
                    src={require("../images/check-in.jpg")}
                    className="w-24 mb-4"
                    alt=""
                  />
                  <h3>Staff / Student card</h3>
                  <p className="text-gray-500 mb-4">
                    Tap your staff / student card on the card reader for a
                    faster login.
                  </p>
                  <button
                    type="button"
                    className={clsx(
                      "text-blue-800 bg-blue-100 hover:bg-blue-200 focus:ring-4 focus:outline-none focus:ring-blue-300 font-bold rounded-lg px-5 py-3 text-center"
                    )}
                    onClick={onShowReadCard}
                  >
                    Tap card
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Transition appear show={readCardActive} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onReadCardClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="absolute top-0 right-0 pt-4 pr-4">
                    <button
                      className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-500"
                      onClick={onReadCardClose}
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        ></path>
                      </svg>
                    </button>
                  </div>
                  <div></div>
                  <div className="space-y-4 text-base flex flex-col items-center justify-center">
                    <img
                      src={require("../images/nfc.gif")}
                      className="w-32"
                      alt=""
                    />
                    <h3>Tap your staff / student card on the card reader</h3>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}
